import React, { useState, useRef, useEffect } from 'react';
import { Building2, Send, Image as ImageIcon, X, Map, Upload, LogOut, FileText, Package, User, History, Users, Save, RotateCcw, Settings, Share2, LayoutDashboard, Globe, Sparkles, Plus, Clock, Mail, ArrowLeft, ArrowRight, Calendar, CheckCircle, Loader2, AlertCircle, Briefcase } from 'lucide-react';
import { useNavigate, Navigate } from 'react-router-dom';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, updateDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { auth, db, storage } from './firebase';
import Select from 'react-select';
import toast, { Toaster } from 'react-hot-toast';
import { cities, neighborhoods, streets } from './data';
import { processImages, compressBlob } from './utils/imageCompression';
import { useNetworkNotifications } from './utils/useNetworkNotifications';
import { triggerMatchDetection, propertyRelevantFieldsChanged } from './utils/smartAlerts';
import NotificationBell from './components/NotificationBell';
import ReportHistoryModal from './components/ReportHistoryModal';
import NeighborhoodMap from './components/NeighborhoodMap';
import DateDisplay from './components/DateDisplay';
import ClientCRM from './components/ClientCRM';
import type { Property, Option, NearbyPlace, UserSubscription, UserProfile } from './types';
import { useEntitlement } from './utils/entitlement';
import { OwnerExpiredBanner } from './components/CommercialGate';

const MAKE_PROPERTY_WEBHOOK_URL = import.meta.env.VITE_MAKE_PROPERTY_WEBHOOK_URL;
if (!MAKE_PROPERTY_WEBHOOK_URL) {
  console.warn('Missing VITE_MAKE_PROPERTY_WEBHOOK_URL');
}
const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
if (!GOOGLE_MAPS_API_KEY) {
  console.warn('Missing VITE_GOOGLE_MAPS_API_KEY');
}
const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-script';
const LAST_EMAIL_KEY = 'lastUsedEmail';
const MAX_DESCRIPTION_LENGTH = 250;

const propertyTypes = [
  { value: 'apartment', label: 'דירת מגורים' },
  { value: 'duplex', label: 'דופלקס' },
  { value: 'penthouse', label: 'פנטהאוז' },
  { value: 'garden-apartment', label: 'דירת גן' },
  { value: 'house', label: 'בית פרטי' },
  { value: 'commercial', label: 'מסחרי' },
];

const propertyAgeOptions = [
  { value: 'new_0_5', label: 'חדש / 0-5 שנים' },
  { value: 'years_6_10', label: '6-10 שנים' },
  { value: 'years_11_20', label: '11-20 שנים' },
  { value: 'years_21_30', label: '21-30 שנים' },
  { value: 'years_31_40', label: '31-40 שנים' },
  { value: 'years_41_50', label: '41-50 שנים' },
  { value: 'years_50_plus', label: '50+ שנים' },
  { value: 'pinui_binui', label: 'פינוי-בינוי / בניין חדש' },
  { value: 'tama38_2', label: 'תמ"א 38/2 הריסה ובנייה' },
  { value: 'tama38_1', label: 'תמ"א 38/1 חיזוק ותוספת' },
  { value: 'unknown', label: 'לא ידוע' },
];

const PROPERTY_AGE_LEGACY_MAP: Record<string, string> = {
  '0-2': 'new_0_5',
  '3-8': 'years_6_10',
  '9-15': 'years_11_20',
  '16-25': 'years_21_30',
  '26-45': 'years_31_40',
  '46+': 'years_50_plus',
};

const propertyConditionOptions = [
  { value: 'renovated_high', label: 'משופץ ברמה גבוהה' },
  { value: 'renovated', label: 'משופץ' },
  { value: 'good', label: 'שמור' },
  { value: 'original', label: 'מצב מקורי' },
  { value: 'needs_renovation', label: 'דורש שיפוץ' },
  { value: 'unknown', label: 'לא ידוע' },
];

const viewOptions = [
  { value: 'sea', label: 'נוף לים' },
  { value: 'park', label: 'נוף לפארק / שטח ירוק' },
  { value: 'open', label: 'נוף פתוח-קומה גבוהה' },
  { value: 'city', label: 'נוף אורבני / קו רקיע' },
  { value: 'without', label: 'ללא חשיפה לנוף' },
  { value: 'roaud', label: 'לכביש ראשי' }
];

const airDirectionOptions = [
  { value: 'south-east', label: 'דרום מזרח' },
  { value: 'south-east', label: 'דרום מערב' },
  { value: 'north', label: 'צפון מזרח ודרום' },
  { value: 'north-east', label: 'צפון מערב ודרום' },
  { value: 'west', label: 'צפון מזרח' },
  { value: 'west', label: 'צפון מערב' },
  { value: 'north-west', label: 'דרום מזרח ומערב' },
  { value: 'south-west', label: 'צפון מזרח ומערב' },
  { value: 'south-west', label: 'ארבע כיווני אוויר' } 
];

const PLACE_CATEGORIES = [
  { type: 'beach', label: 'חופים' },
  { type: 'museum', label: 'מוזיאונים' },
  { type: 'local_government_office', label: 'משרדי ממשלה' },
  { type: 'parking', label: 'חניונים' },
  { type: 'transit_station', label: 'תחבורה ציבורית' },
  { type: 'school', label: 'מוסדות חינוך' },
  { type: 'hospital', label: 'מוסדות בריאות' },
  { type: 'park', label: 'פארקים' },
  { type: 'shopping_mall', label: 'קניונים' },
  { type: 'restaurant', label: 'מסעדות' },
];

function App() {
  const navigate = useNavigate();
  const networkNotifs = useNetworkNotifications();
  const { entitlement, resolutionState: entResolutionState, refresh: refreshEntitlement } = useEntitlement();
  const [creatingOffice, setCreatingOffice] = useState(false);

  // Sync officeRole/officeName from entitlement so they update immediately when
  // the entitlement refreshes (e.g. after create-office), instead of relying on
  // a separate fire-and-forget get-office fetch that races with entitlement.
  useEffect(() => {
    if (entitlement.office_id) {
      setOfficeRole(entitlement.my_role);
      setOfficeName(entitlement.office_name);
      setOfficeChecked(true);
    } else if (entResolutionState !== 'loading') {
      setOfficeRole(null);
      setOfficeName(null);
      setOfficeChecked(true);
    }
  }, [entitlement.office_id, entitlement.my_role, entitlement.office_name, entResolutionState]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedCity, setSelectedCity] = useState<Option | null>(null);
  const [selectedNeighborhood, setSelectedNeighborhood] = useState<Option | null>(null);
  const [selectedStreet, setSelectedStreet] = useState<Option | null>(null);
  const [selectedPropertyType, setSelectedPropertyType] = useState<Option | null>(null);
  const [selectedPropertyAge, setSelectedPropertyAge] = useState<Option | null>(null);
  const [selectedPropertyCondition, setSelectedPropertyCondition] = useState<Option | null>(null);
  const [selectedView, setSelectedView] = useState<Option | null>(null);
  const [selectedAirDirection, setSelectedAirDirection] = useState<Option | null>(null);
  const [nearbyPlaces, setNearbyPlaces] = useState<Record<string, NearbyPlace[]>>({});
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userName, setUserName] = useState<string>('');
  const [showHistory, setShowHistory] = useState(false);
  const [showCRM, setShowCRM] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState<string | undefined>(undefined);
  const [autoOpenMatchLeadId, setAutoOpenMatchLeadId] = useState<string | undefined>(undefined);
  const [remainingChars, setRemainingChars] = useState(MAX_DESCRIPTION_LENGTH);
  const [neighborhoodMapUrl, setNeighborhoodMapUrl] = useState<string | null>(null);
  const [neighborhoodDealsCount, setNeighborhoodDealsCount] = useState<number | null>(null);
  const [isLoadedReport, setIsLoadedReport] = useState(false);
  const [lastReportData, setLastReportData] = useState<string | null>(null);
  const [loadedFormId, setLoadedFormId] = useState<string | null>(null);
  const [officeRole, setOfficeRole] = useState<string | null>(null);
  const [officeName, setOfficeName] = useState<string | null>(null);
  const [officeChecked, setOfficeChecked] = useState(false);
  const [onboardingChoice, setOnboardingChoice] = useState<'none' | 'create' | 'create_developer' | 'wait'>('none');
  const [pendingInvitations, setPendingInvitations] = useState<{ id: string; office_name: string; role: string; invite_token: string }[]>([]);
  const [showInvitationDialog, setShowInvitationDialog] = useState(false);
  const [respondingInvite, setRespondingInvite] = useState<string | null>(null);
  const hasOffice = !!entitlement.office_id;

  // Redirect developer users to the developer dashboard.
  // Only fires after entitlement has resolved — not during loading or error states.
  // This prevents developers from seeing the brokerage property form at /.
  if (entResolutionState === 'resolved' && entitlement.account_type === 'developer' && hasOffice) {
    return <Navigate to="/developer" replace />;
  }

  const fileInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const syncedProfileRef = useRef(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const streetViewRef = useRef<HTMLDivElement>(null);
  
  const [streetViewAvailable, setStreetViewAvailable] = useState(false);
  const [streetViewLocation, setStreetViewLocation] = useState<{ lat: number; lng: number; heading: number; pitch: number } | null>(null);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const [currentAddress, setCurrentAddress] = useState<string>('');
  const [savedStreetViewUrl, setSavedStreetViewUrl] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [existingImageUrls, setExistingImageUrls] = useState<string[]>([]);
  const [hasUnsavedImageChanges, setHasUnsavedImageChanges] = useState(false);

  const [formData, setFormData] = useState<Property>({
    city: '',
    cityLabel: '',
    neighborhood: '',
    neighborhoodLabel: '',
    street: '',
    streetLabel: '',
    houseNumber: '',
    rooms: 1,
    floor: 0,
    bathrooms: 1,
    parkingSpots: 0,
    size: 0,
    price: 0,
    advisorPrice: undefined,
    propertyType: '',
    propertyTypeLabel: '',
    description: '',
    email: '',
    images: [],
    propertyAge: '',
    propertyAgeLabel: '',
    propertyCondition: '',
    propertyConditionLabel: '',
    view: '',
    viewLabel: '',
    airDirection: '',
    airDirectionLabel: '',
    hasBalcony: false,
    balconySize: undefined,
    hasElevator: false,
    hasSaferoom: false,
    daysOnMarket: ''
  });

  const normalizeFileName = (name: string): string => {
    return name
      .replace(/"/g, '')
      .replace(/'/g, '')
      .replace(/:/g, '')
      .replace(/\//g, '-')
      .replace(/\\/g, '-')
      .trim();
  };

  const getNeighborhoodMapUrl = async (neighborhood: string, city: string): Promise<string | null> => {
    if (!neighborhood || !city) return null;

    try {
      const normalizedNeighborhood = normalizeFileName(neighborhood);
      const imagePath = `מפות שכונות ${city}/שכונת ${normalizedNeighborhood} ${city}.png`;
      const imageRef = ref(storage, imagePath);
      const url = await getDownloadURL(imageRef);
      return url;
    } catch (error) {
      console.log(`No neighborhood map found for: ${neighborhood}, ${city}`);
      console.log('Neighborhood Map URL:', neighborhoodMapUrl);
      return null;
    }
  };

  // Effect לטעינת מפת השכונה
  useEffect(() => {
    const loadNeighborhoodMap = async () => {
      if (selectedNeighborhood && selectedCity) {
        const url = await getNeighborhoodMapUrl(selectedNeighborhood.label, selectedCity.label);
        setNeighborhoodMapUrl(url);
      } else {
        setNeighborhoodMapUrl(null);
      }
    };

    loadNeighborhoodMap();
  }, [selectedNeighborhood, selectedCity]);

  // Load Google Maps
  const loadGoogleMaps = () => {
    return new Promise<void>((resolve, reject) => {
      if (window.google && window.google.maps) {
        setIsGoogleMapsLoaded(true);
        resolve();
        return;
      }

      const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
      if (existingScript) {
        existingScript.addEventListener('load', () => {
          setIsGoogleMapsLoaded(true);
          resolve();
        });
        return;
      }

      const script = document.createElement('script');
      script.id = GOOGLE_MAPS_SCRIPT_ID;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&language=he`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        setIsGoogleMapsLoaded(true);
        resolve();
      };
      
      script.onerror = () => {
        reject(new Error('Failed to load Google Maps'));
      };
      
      document.head.appendChild(script);
    });
  };

  // Initialize map and street view
  const initializeMapAndStreetView = async (address: string, skipStreetView = false) => {
    if (!window.google || !window.google.maps) {
      console.error('Google Maps not loaded');
      return;
    }

    setMapLoading(true);
    setCurrentAddress(address);

    try {
      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const location = results[0].geometry.location;
          const lat = location.lat();
          const lng = location.lng();

          setStreetViewLocation({ lat, lng, heading: 0, pitch: 0 });

          // Create map
          if (mapRef.current) {
            const map = new window.google.maps.Map(mapRef.current, {
              center: { lat, lng },
              zoom: 16,
              mapTypeId: window.google.maps.MapTypeId.ROADMAP,
            });

            // Add marker
            new window.google.maps.Marker({
              position: { lat, lng },
              map: map,
              title: address,
            });
          }

          // Create street view (skip if saved image exists)
          if (!skipStreetView && streetViewRef.current) {
            const streetViewService = new window.google.maps.StreetViewService();

            streetViewService.getPanorama({
              location: { lat, lng },
              radius: 50,
            }, (data, svStatus) => {
              if (svStatus === 'OK') {
                setStreetViewAvailable(true);

                const panorama = new window.google.maps.StreetViewPanorama(streetViewRef.current!, {
                  position: { lat, lng },
                  pov: { heading: 0, pitch: 0 },
                  zoom: 1,
                });

                panorama.addListener('pov_changed', () => {
                  const pov = panorama.getPov();
                  setStreetViewLocation({ lat, lng, heading: pov.heading, pitch: pov.pitch });
                });
              } else {
                setStreetViewAvailable(false);
              }
            });
          }

          // Search for nearby places
          searchNearbyPlaces({ lat, lng });
        } else {
          console.error('Geocoding failed:', status);
        }
        setMapLoading(false);
      });
    } catch (error) {
      console.error('Error initializing map:', error);
      setMapLoading(false);
    }
  };

  // Search nearby places
  const searchNearbyPlaces = (location: { lat: number; lng: number }) => {
    if (!window.google || !window.google.maps) return;

    const service = new window.google.maps.places.PlacesService(document.createElement('div'));
    const newNearbyPlaces: Record<string, NearbyPlace[]> = {};

    PLACE_CATEGORIES.forEach(category => {
      const request = {
        location: new window.google.maps.LatLng(location.lat, location.lng),
        radius: 1000,
        type: category.type as any,
      };

      service.nearbySearch(request, (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
          const places = results.slice(0, 3).map(place => ({
            name: place.name || '',
            address: place.vicinity || '',
            type: category.type,
            label: category.label,
            rating: place.rating || 0,
            distance: 0,
          }));
          
          newNearbyPlaces[category.type] = places;
          setNearbyPlaces(prev => ({ ...prev, [category.type]: places }));
        }
      });
    });
  };

  // Effect to load Google Maps on component mount
  useEffect(() => {
    loadGoogleMaps().catch(console.error);
  }, []);

  // Clear saved street view when user edits address fields on a loaded report
  const prevAddressRef = useRef<string>('');
  useEffect(() => {
    if (!selectedStreet || !formData.houseNumber) return;
    const currentAddr = `${selectedStreet.label} ${formData.houseNumber} ${selectedCity?.label || ''}`;
    if (prevAddressRef.current && prevAddressRef.current !== currentAddr && savedStreetViewUrl) {
      setSavedStreetViewUrl(null);
    }
    prevAddressRef.current = currentAddr;
  }, [selectedStreet, formData.houseNumber, selectedCity]);

  // Effect to initialize map when address changes
  useEffect(() => {
    if (!isGoogleMapsLoaded || !selectedStreet || !formData.houseNumber) return;

    const address = `${selectedStreet.label} ${formData.houseNumber}, ${selectedCity?.label}, Israel`;
    console.log("StreetView source:", savedStreetViewUrl || "LIVE");
    initializeMapAndStreetView(address, !!savedStreetViewUrl);
  }, [isGoogleMapsLoaded, selectedStreet, formData.houseNumber, selectedCity, savedStreetViewUrl]);

  const formatPrice = (value: string): string => {
    const numericValue = value.replace(/[^\d]/g, '');
    
    if (!numericValue) return '';
    
    const number = parseInt(numericValue, 10);
    return number.toLocaleString('he-IL');
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numericValue = parseInt(value.replace(/[^\d]/g, ''), 10);
    
    setFormData(prev => ({
      ...prev,
      price: isNaN(numericValue) ? 0 : numericValue
    }));
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, description: value }));
    setRemainingChars(MAX_DESCRIPTION_LENGTH - value.length);

    // Auto-suggest condition from description — only if user hasn't already selected one
    if (!selectedPropertyCondition) {
      const lower = value.toLowerCase();
      const renovationKeywords = /משופץ|משופצת|שופץ|שופצה/;
      if (renovationKeywords.test(lower)) {
        const suggested = propertyConditionOptions.find(o => o.value === 'renovated')!;
        setSelectedPropertyCondition(suggested);
        setFormData(prev => ({
          ...prev,
          propertyCondition: suggested.value,
          propertyConditionLabel: suggested.label
        }));
      }
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const newImages = Array.from(e.target.files);
    if (existingImageUrls.length + formData.images.length + newImages.length > 5) {
      toast.error('ניתן להעלות עד 5 תמונות');
      return;
    }

    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...newImages]
    }));
    setHasUnsavedImageChanges(true);
  };

  const removeImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
    setHasUnsavedImageChanges(true);
  };

  const removeExistingImage = (index: number) => {
    setExistingImageUrls(prev => prev.filter((_, i) => i !== index));
    setHasUnsavedImageChanges(true);
  };

  const confirmDiscardImages = (): boolean => {
    if (!hasUnsavedImageChanges) return true;
    return window.confirm('יש שינויים שלא נשמרו. לצאת בלי לשמור?');
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const [compressedLogo] = await processImages([file]);
        if (!compressedLogo) {
          throw new Error('Failed to compress logo');
        }

        setLogoFile(compressedLogo);
        const logoUrl = await uploadLogo(compressedLogo);
        if (userProfile) {
          setUserProfile({
            ...userProfile,
            logoUrl
          });
        }
        toast.success('הלוגו הועלה בהצלחה');
      } catch (error) {
        console.error('Error uploading logo:', error);
        toast.error('שגיאה בהעלאת הלוגו');
        setLogoFile(null);
      }
    }
  };

  const uploadLogo = async (file: File): Promise<string> => {
    if (!auth.currentUser) throw new Error('No authenticated user');
    
    const fileExtension = file.name.split('.').pop();    
    const fileName = `logos/${auth.currentUser.uid}/${uuidv4()}.${fileExtension}`;
    const storageRef = ref(storage, fileName);

    if (userProfile?.logoUrl) {
      try {
        const oldLogoRef = ref(storage, userProfile.logoUrl);
        await deleteObject(oldLogoRef);
      } catch (error) {
        console.error('Error deleting old logo:', error);
      }
    }

    await uploadBytes(storageRef, file);
    const downloadUrl = await getDownloadURL(storageRef);

    const userRef = doc(db, 'users', auth.currentUser.uid);
    await updateDoc(userRef, {
      logoUrl: downloadUrl,
      updatedAt: new Date().toISOString()
    });

    return downloadUrl;
  };

  const removeLogo = async () => {
    if (!auth.currentUser || !userProfile?.logoUrl) return;

    try {
      const logoRef = ref(storage, userProfile.logoUrl);
      await deleteObject(logoRef);

      const userRef = doc(db, 'users', auth.currentUser.uid);
      await updateDoc(userRef, {
        logoUrl: null,
        updatedAt: new Date().toISOString()
      });

      setLogoFile(null);
      setUserProfile(prev => prev ? { ...prev, logoUrl: undefined } : null);
      
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
      
      toast.success('הלוגו הוסר בהצלחה');
    } catch (error) {
      console.error('Error removing logo:', error);
      toast.error('שגיאה בהסרת הלוגו');
    }
  };

  useEffect(() => {
    // Track the active Firestore listener so we can tear it down when
    // the auth state changes (logout / different user login).
    let firestoreUnsubscribe: (() => void) | undefined;

    const authUnsubscribe = onAuthStateChanged(auth, (user) => {
      // Always clean up the previous Firestore listener first
      firestoreUnsubscribe?.();
      firestoreUnsubscribe = undefined;

      if (!user) return;

      // Reset sync guard for the new session
      syncedProfileRef.current = false;

      const userDocRef = doc(db, 'users', user.uid);

      firestoreUnsubscribe = onSnapshot(userDocRef, (docSnapshot) => {
        try {
          if (docSnapshot.exists()) {
            const userData = docSnapshot.data() as UserProfile;

            // Fix missing ads fields automatically
            if (userData.subscription &&
                (userData.subscription.adsTotal === undefined ||
                 userData.subscription.adsUsed === undefined)) {
              console.log('🔧 Fixing missing ads fields...');
              (async () => {
                try {
                  await updateDoc(userDocRef, {
                    'subscription.adsTotal': userData.subscription.reportsTotal,
                    'subscription.adsUsed': 0,
                    updatedAt: new Date().toISOString()
                  });
                  console.log('✅ Fixed successfully!');
                } catch (error) {
                  console.error('Error fixing ads fields:', error);
                }
              })();
              // The onSnapshot will trigger again with updated data
              return;
            }

            console.log('=== Firebase User Data ===');
            console.log('Full userData:', userData);
            console.log('userData.subscription:', userData.subscription);
            console.log('adsTotal:', userData.subscription?.adsTotal);
            console.log('adsUsed:', userData.subscription?.adsUsed);

            const subscriptionData = {
              ...userData.subscription,
              adsTotal: userData.subscription?.adsTotal ?? 0,
              adsUsed: userData.subscription?.adsUsed ?? 0
            };

            console.log('Final subscriptionData:', subscriptionData);
            console.log('Remaining ads:', subscriptionData.adsTotal - subscriptionData.adsUsed);
            console.log('========================');

            setUserProfile(userData);
            setSubscription(subscriptionData);
            setUserName(userData.fullName || '');
            setFormData(prev => ({
              ...prev,
              email: userData.email || ''
            }));

            // Sync profile to Supabase once per auth session
            if (userData.email && !syncedProfileRef.current) {
              syncedProfileRef.current = true;

              // Log the phone value we're about to sync so mismatches are visible
              const phoneToSync = userData.phone || null;
              console.log('📞 sync-profile phone field from Firestore:', JSON.stringify(phoneToSync));

              user.getIdToken().then(token => {
                fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-profile`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email: userData.email, display_name: userData.fullName || null, phone: phoneToSync }),
                })
                  .then(r => r.json())
                  .then(d => console.log('sync-profile response:', d))
                  .catch(err => console.error('sync-profile failed:', err));

                // Fetch office role once per session to gate "ניהול משרד" button
                fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-office`, {
                  headers: { Authorization: `Bearer ${token}` },
                })
                  .then(r => r.json())
                  .then(data => {
                    if (data.success && data.office) {
                      setOfficeRole(data.office.myRole as string);
                      setOfficeName(data.office.name as string);
                    } else {
                      setOfficeRole(null);
                      setOfficeName(null);
                    }
                    setOfficeChecked(true);
                  })
                  .catch(err => { console.error('get-office failed:', err); setOfficeChecked(true); });

                // Check for pending office invitations
                fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-pending-invitations`, {
                  headers: { Authorization: `Bearer ${token}` },
                })
                  .then(r => r.json())
                  .then(data => {
                    if (data.success && data.invitations?.length > 0) {
                      setPendingInvitations(data.invitations);
                      setShowInvitationDialog(true);
                    }
                  })
                  .catch(err => console.error('get-pending-invitations failed:', err));
              });
            }
          } else {
            console.error('User document does not exist!');
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          toast.error('שגיאה בטעינת נתוני המשתמש');
        }
      }, (error) => {
        console.error('Error listening to user data:', error);
        toast.error('שגיאה בהאזנה לנתוני המשתמש');
      });
    });

    return () => {
      authUnsubscribe();
      firestoreUnsubscribe?.();
    };
  }, []);

  const handleAcceptInvitation = async (inviteToken: string) => {
    try {
      setRespondingInvite(inviteToken);
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/accept-invitation`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: inviteToken }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.error_code === 'SEAT_LIMIT_REACHED') {
          toast.error('המשרד הגיע למגבלת 10 המשתמשים בתקופת הניסיון. לא ניתן לצרף משתמש נוסף בשלב זה.');
        } else if (data.error_code === 'EMAIL_MISMATCH') {
          toast.error('כתובת המייל אינה תואמת להזמנה');
        } else if (data.error_code === 'ALREADY_IN_ANOTHER_OFFICE') {
          toast.error('כבר משויך למשרד אחר. אין ניתן להצטרף למשרד נוסף.');
        } else {
          toast.error(data.error || 'שגיאה באישור ההזמנה');
        }
        return;
      }
      toast.success('הצטרפת למשרד בהצלחה');
      setPendingInvitations([]);
      setShowInvitationDialog(false);
      await refreshEntitlement();
    } catch {
      toast.error('שגיאה באישור ההזמנה');
    } finally {
      setRespondingInvite(null);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
      toast.success('התנתקת בהצלחה');
    } catch (error) {
      console.error('Error during logout:', error);
      toast.error('אירעה שגיאה בהתנתקות');
    }
  };

  const handleLoadData = async (formId: string) => {
    try {
      const formDoc = await getDoc(doc(db, 'forms', formId));
      if (!formDoc.exists()) {
        toast.error('לא נמצאו נתונים');
        return;
      }

      const data = formDoc.data();

      // Reset all selections and form data first to clear previous values
      setSelectedCity(null);
      setSelectedNeighborhood(null);
      setSelectedStreet(null);
      setSelectedPropertyType(null);
      setSelectedPropertyAge(null);
      setSelectedView(null);
      setSelectedAirDirection(null);

      const cityOption = cities.find(c => c.label === data.address?.city || c.value === data.address?.city);
      if (cityOption) {
        setSelectedCity(cityOption);
      }

      if (cityOption?.value) {
        const neighborhoodOptions = neighborhoods[cityOption.value] || [];
        const neighborhoodOption = neighborhoodOptions.find(n => n.label === data.address?.neighborhood || n.value === data.address?.neighborhood);
        if (neighborhoodOption) {
          setSelectedNeighborhood(neighborhoodOption);
        }

        if (neighborhoodOption?.value) {
          const streetOptions = streets[cityOption.value]?.[neighborhoodOption.value] || [];
          const streetOption = streetOptions.find(s =>
            s.label === data.streetLabel ||
            s.label === data.street ||
            s.value === data.street ||
            s.label === data.address?.street ||
            s.value === data.address?.street
          );
          if (streetOption) {
            setSelectedStreet(streetOption);
          } else if (data.street || data.streetLabel) {
            // אם הרחוב לא נמצא ברשימה, צור אובייקט חדש
            setSelectedStreet({
              value: data.street || data.address?.street || '',
              label: data.streetLabel || data.street || data.address?.street || ''
            });
          }
        }
      }

      const propertyTypeOption = propertyTypes.find(p => p.value === data.property?.propertyType);
      if (propertyTypeOption) {
        setSelectedPropertyType(propertyTypeOption);
      }

      const rawAge = data.property?.propertyAge || '';
      const mappedAge = PROPERTY_AGE_LEGACY_MAP[rawAge] ?? rawAge;
      const propertyAgeOption = propertyAgeOptions.find(a => a.value === mappedAge);
      if (propertyAgeOption) {
        setSelectedPropertyAge(propertyAgeOption);
      }

      const propertyConditionOption = propertyConditionOptions.find(c => c.value === data.property?.propertyCondition);
      if (propertyConditionOption) {
        setSelectedPropertyCondition(propertyConditionOption);
      }

      const viewOption = viewOptions.find(v => v.value === data.property?.view);
      if (viewOption) {
        setSelectedView(viewOption);
      }

      const airDirectionOption = airDirectionOptions.find(a => a.value === data.property?.airDirection);
      if (airDirectionOption) {
        setSelectedAirDirection(airDirectionOption);
      }

      setFormData({
        city: data.address?.city || '',
        cityLabel: data.address?.city || '',
        neighborhood: data.address?.neighborhood || '',
        neighborhoodLabel: data.address?.neighborhood || '',
        street: data.street || data.address?.street || '',
        streetLabel: data.streetLabel || data.address?.street || '',
        houseNumber: data.address?.houseNumber || '',
        price: data.property?.price || 0,
        rooms: Number(data.property?.rooms) || 1,
        bathrooms: data.property?.bathrooms || 1,
        floor: data.property?.floor || 0,
        totalFloors: data.property?.totalFloors || 0,
        size: data.size || data.property?.size || data.property?.sqm || 0,
        parkingSpots: data.property?.parkingSpots || 0,
        balconySize: data.property?.balconySize || undefined,
        hasBalcony: data.property?.hasBalcony || false,
        hasElevator: data.property?.hasElevator || false,
        hasSaferoom: data.property?.hasSaferoom || false,
        propertyType: data.property?.propertyType || '',
        propertyTypeLabel: propertyTypeOption?.label || '',
        propertyAge: mappedAge,
        propertyAgeLabel: propertyAgeOption?.label || '',
        propertyCondition: data.property?.propertyCondition || '',
        propertyConditionLabel: propertyConditionOption?.label || '',
        view: data.property?.view || '',
        viewLabel: viewOption?.label || '',
        airDirection: data.property?.airDirection || '',
        airDirectionLabel: airDirectionOption?.label || '',
        advisorPrice: data.property?.advisorPrice || undefined,
        description: data.property?.description || '',
        daysOnMarket: data.property?.daysOnMarket || '',
        email: formData.email,
        images: []
      });
      setExistingImageUrls(data.imageUrls?.propertyImages || []);
      setLoadedFormId(formId);
      setHasUnsavedImageChanges(false);

      if (data.imageUrls?.streetView) {
        setSavedStreetViewUrl(data.imageUrls.streetView);
      } else {
        setSavedStreetViewUrl(null);
      }

      setIsLoadedReport(true);
      toast.success('נתוני הנכס נטענו בהצלחה');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('שגיאה בטעינת נתוני הנכס');
    }
  };

  const resetForm = () => {
    setExistingImageUrls([]);
    setLoadedFormId(null);
    setHasUnsavedImageChanges(false);
    // Reset all selections
    setSelectedCity(null);
    setSelectedNeighborhood(null);
    setSelectedStreet(null);
    setSelectedPropertyType(null);
    setSelectedPropertyAge(null);
    setSelectedPropertyCondition(null);
    setSelectedView(null);
    setSelectedAirDirection(null);

    // Reset form data
    setFormData({
      city: '',
      cityLabel: '',
      neighborhood: '',
      neighborhoodLabel: '',
      street: '',
      streetLabel: '',
      houseNumber: '',
      rooms: 1,
      floor: 0,
      bathrooms: 1,
      parkingSpots: 0,
      size: 0,
      price: 0,
      advisorPrice: undefined,
      totalFloors: 0,
      balconySize: undefined,
      hasBalcony: false,
      hasElevator: false,
      hasSaferoom: false,
      propertyType: '',
      propertyTypeLabel: '',
      propertyAge: '',
      propertyAgeLabel: '',
      propertyCondition: '',
      propertyConditionLabel: '',
      view: '',
      viewLabel: '',
      airDirection: '',
      airDirectionLabel: '',
      email: userProfile?.email || '',
      images: [],
      description: '',
      daysOnMarket: ''
    });

    // Reset nearby places
    setNearbyPlaces({});

    // Clear map and street view
    if (mapRef.current) {
      mapRef.current.innerHTML = '';
    }
    if (streetViewRef.current) {
      streetViewRef.current.innerHTML = '';
    }
    setStreetViewAvailable(false);
    setStreetViewLocation(null);
    setSavedStreetViewUrl(null);
    setIsLoadedReport(false);
    setLastReportData(null);

    toast.success('הטופס אופס בהצלחה');
  };

  const generateFormId = (userPhone: string): string => {
    const now = new Date();
    const year = now.getFullYear().toString();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const seconds = now.getSeconds().toString().padStart(2, '0');
    
    return `${userPhone}_${year}${month}${day}_${hours}${minutes}${seconds}`;
  };

  const updateReportCount = async (): Promise<boolean> => {
    if (!auth.currentUser || !subscription) return false;

    if (subscription.reportsUsed >= subscription.reportsTotal) {
      toast.error('הגעת למגבלת הדוחות שלך. אנא שדרג את החבילה.');
      return false;
    }

    try {
      const userRef = doc(db, 'users', auth.currentUser.uid);
      await updateDoc(userRef, {
        'subscription.reportsUsed': subscription.reportsUsed + 1,
        updatedAt: new Date().toISOString()
      });

      setSubscription(prev => prev ? {
        ...prev,
        reportsUsed: prev.reportsUsed + 1
      } : null);

      return true;
    } catch (error) {
      console.error('Error updating report count:', error);
      toast.error('שגיאה בעדכון מספר הדוחות');
      return false;
    }
  };

  const updateCreateAdCount = async (): Promise<void> => {
    if (!auth.currentUser) {
      console.log('No authenticated user');
      return;
    }

    try {
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const currentCount = userProfile?.createAdCount || 0;

      console.log('Updating createAdCount:', { currentCount, newCount: currentCount + 1, userId: auth.currentUser.uid });

      await setDoc(userRef, {
        createAdCount: currentCount + 1,
        updatedAt: new Date().toISOString()
      }, { merge: true });

      console.log('Successfully updated createAdCount in Firestore');

      setUserProfile(prev => prev ? {
        ...prev,
        createAdCount: currentCount + 1
      } : null);
    } catch (error) {
      console.error('Error updating create ad count:', error);
      toast.error('שגיאה בעדכון מונה המודעות');
    }
  };

  const updateAdsCount = async (): Promise<boolean> => {
    if (!auth.currentUser || !subscription) {
      console.log('Cannot update ads: no user or subscription');
      return false;
    }

    console.log('=== Before Update ===');
    console.log('Current subscription:', subscription);
    console.log('adsUsed:', subscription.adsUsed);
    console.log('adsTotal:', subscription.adsTotal);

    if (subscription.adsUsed >= subscription.adsTotal) {
      console.log('Ad limit reached!');
      toast.error('הגעת למגבלת המודעות שלך. אנא שדרג את החבילה.');
      return false;
    }

    try {
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const newAdsUsed = subscription.adsUsed + 1;

      console.log('Updating Firebase with new adsUsed:', newAdsUsed);

      await updateDoc(userRef, {
        'subscription.adsUsed': newAdsUsed,
        updatedAt: new Date().toISOString()
      });

      console.log('Firebase updated successfully');

      setSubscription(prev => prev ? {
        ...prev,
        adsUsed: newAdsUsed
      } : null);

      return true;
    } catch (error) {
      console.error('Error updating ads count:', error);
      toast.error('שגיאה בעדכון מספר המודעות');
      return false;
    }
  };

  const validateForm = () => {
    // בדיקת שדות טקסט חובה
    const requiredTextFields = ['city', 'neighborhood', 'street', 'houseNumber', 'email'];
    
    for (const field of requiredTextFields) {
      if (!formData[field as keyof Property]) {
        const fieldNames: { [key: string]: string } = {
          city: 'עיר',
          neighborhood: 'שכונה',
          street: 'רחוב',
          houseNumber: 'מספר בית',
          email: 'אימייל'
        };
        toast.error(`שדה ${fieldNames[field]} הוא חובה`);
        return false;
      }
    }
    
    // בדיקת שדות מספריים חובה
    console.log("Rooms value:", formData.rooms, typeof formData.rooms);
    if (formData.rooms === undefined || formData.rooms === null || Number(formData.rooms) <= 0) {
      toast.error('מספר חדרים הוא חובה וחייב להיות גדול מ-0');
      return false;
    }
    if (formData.floor === undefined || formData.floor === null || formData.floor < 0 || formData.floor > 70) {
      toast.error('מספר הקומה חייב להיות בין 0 ל-70 (0 = קרקע)');
      return false;
    }
    
    if (!formData.bathrooms || formData.bathrooms <= 0) {
      toast.error('מספר חדרי רחצה הוא חובה וחייב להיות גדול מ-0');
      return false;
    }
    
    if (formData.parkingSpots < 0) {
      toast.error('מספר מקומות חניה לא יכול להיות שלילי');
      return false;
    }
    
    if (!formData.size || formData.size <= 0) {
      toast.error('גודל הנכס הוא חובה ויגב להיות גדול מ-0');
      return false;
    }
    
    if (!formData.price || formData.price <= 0) {
      toast.error('מחיר הנכס הוא חובה ויגב להיות גדול מ-0');
      return false;
    }
    
    // בדיקת שדות בחירה חובה
    if (!formData.propertyType) {
      toast.error('סוג נכס הוא חובה');
      return false;
    }
    
    if (!formData.propertyAge) {
      toast.error('גיל הנכס הוא חובה');
      return false;
    }
    
    if (!formData.view) {
      toast.error('נוף הוא חובה');
      return false;
    }
    
    if (!formData.airDirection) {
      toast.error('כיוון אוויר הוא חובה');
      return false;
    }
    
    const daysOnMarketValue = Number(formData.daysOnMarket);
    if (formData.daysOnMarket === '' || isNaN(daysOnMarketValue) || daysOnMarketValue < 0) {
      toast.error('ימים בשוק הוא חובה וחייב להיות 0 או יותר');
      return false;
    }

    // בדיקת תקינות אימייל
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('כתובת האימייל אינה תקינה');
      return false;
    }
    
    // בדיקת גודל מרפסת אם יש מרפסת
    if (formData.hasBalcony && (!formData.balconySize || formData.balconySize <= 0)) {
      toast.error('אם יש מרפסת, יש לציין את גודלה');
      return false;
    }
    
    return true;
  };

  const saveFormImages = async (formId: string, streetViewImage?: File, staticMapImage?: File) => {
    const imageUrls = {
      propertyImages: [...existingImageUrls] as string[],
      streetView: undefined as string | undefined,
      staticMap: undefined as string | undefined
    };

    console.log('[saveFormImages] existing:', existingImageUrls.length,
      '| new files:', formData.images.length,
      '| starting propertyImages count:', imageUrls.propertyImages.length);

    try {
      if (formData.images.length > 0) {
        const compressedImages = await processImages(formData.images);

        for (let i = 0; i < compressedImages.length; i++) {
          const image = compressedImages[i];
          const fileExtension = image.name.split('.').pop();
          const fileName = `forms/${formId}/property-${existingImageUrls.length + i + 1}.${fileExtension}`;
          console.log('[saveFormImages] uploading:', fileName);
          const storageRef = ref(storage, fileName);

          await uploadBytes(storageRef, image);
          const downloadUrl = await getDownloadURL(storageRef);
          imageUrls.propertyImages.push(downloadUrl);
        }
      }
      if (streetViewImage) {
        const fileName = `forms/${formId}/street-view.jpg`;
        const storageRef = ref(storage, fileName);
        await uploadBytes(storageRef, streetViewImage);
        imageUrls.streetView = await getDownloadURL(storageRef);
      }




      if (staticMapImage) {
        const fileName = `forms/${formId}/static-map.jpg`;
        const storageRef = ref(storage, fileName);
        await uploadBytes(storageRef, staticMapImage);
        imageUrls.staticMap = await getDownloadURL(storageRef);
      }

      // Create a copy of formData without the images array (which contains File objects)
      // and ensure undefined values are converted to null for Firestore compatibility
      const propertyDataForFirestore = {
        ...formData,
        balconySize: formData.balconySize ?? null,
        advisorPrice: formData.advisorPrice ?? null
      };
      delete propertyDataForFirestore.images;

      // בדיקה אם הדוח כבר קיים
      const formRef = doc(db, 'forms', formId);
      const existingDoc = await getDoc(formRef);

      let reportId: string;
      let generatedAt: string;
      let version: number;
      let createdAt: string;

      if (existingDoc.exists()) {
        // דוח קיים - שמירת reportId ועדכון גרסה
        const existingData = existingDoc.data();
        reportId = existingData.reportId || formId;
        generatedAt = existingData.generatedAt || existingData.timestamp || new Date().toISOString();
        version = (existingData.version || 1) + 1;
        createdAt = existingData.createdAt || existingData.timestamp || new Date().toISOString();

        await setDoc(formRef, {
          formId,
          userId: auth.currentUser?.uid,
          createdAt,
          reportId,
          generatedAt,
          lastModified: new Date().toISOString(),
          version,
          status: 'completed',
          street: selectedStreet?.value,
          streetLabel: selectedStreet?.label,
          address: {
            city: selectedCity?.label,
            neighborhood: selectedNeighborhood?.label,
            street: selectedStreet?.label,
            houseNumber: formData.houseNumber
          },
          property: propertyDataForFirestore,
          imageUrls,
          nearbyPlaces
        });
      } else {
        // דוח חדש - יצירת reportId חדש
        const now = new Date().toISOString();
        reportId = formId;
        generatedAt = now;
        version = 1;
        createdAt = now;

        await setDoc(formRef, {
          formId,
          userId: auth.currentUser?.uid,
          createdAt,
          reportId,
          generatedAt,
          lastModified: now,
          version,
          status: 'completed',
          street: selectedStreet?.value,
          streetLabel: selectedStreet?.label,
          address: {
            city: selectedCity?.label,
            neighborhood: selectedNeighborhood?.label,
            street: selectedStreet?.label,
            houseNumber: formData.houseNumber
          },
          property: propertyDataForFirestore,
          imageUrls,
          nearbyPlaces
        });
      }

    } catch (error) {
      console.error('Error saving form images:', error);
      throw error;
    }

    return imageUrls;
  };

  const getStreetViewImage = async (): Promise<File> => {
    if (!streetViewLocation) {
      throw new Error('Street view location not available');
    }
    const { lat, lng, heading, pitch } = streetViewLocation;
    const url = `https://maps.googleapis.com/maps/api/streetview?size=800x600&location=${lat},${lng}&heading=${heading}&pitch=${pitch}&key=${GOOGLE_MAPS_API_KEY}`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Failed to fetch Street View Static image');
    }
    const blob = await response.blob();
    return new File([blob], 'street-view.jpg', { type: 'image/jpeg' });
  };

  const getStaticMapImage = async (lat: number, lng: number): Promise<File> => {
    const mapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=16&size=600x400&markers=color:red%7C${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`;
    
    try {
      const response = await fetch(mapUrl);
      const blob = await response.blob();
      return new File([blob], 'static-map.jpg', { type: 'image/jpeg' });
    } catch (error) {
      console.error('Error getting static map image:', error);
      throw error;
    }
  };

  const handleSaveData = async () => {
    if (!auth.currentUser) {
      toast.error('יש להתחבר מחדש');
      return;
    }

    if (isSaving) {
      return;
    }

    try {
      setIsSaving(true);

      if (!validateForm()) {
        return;
      }

      const formId = loadedFormId ?? generateFormId(userProfile?.phone || '');
      console.log('[Save] formId:', formId, '| loaded?', !!loadedFormId,
        '| existingImages:', existingImageUrls.length,
        '| newImages:', formData.images.length);

      const propertyDataForFirestore = {
        ...formData,
        balconySize: formData.balconySize ?? null,
        advisorPrice: formData.advisorPrice ?? null
      };
      delete propertyDataForFirestore.images;

      let streetViewImage: File | undefined;
      let staticMapImage: File | undefined;

      if (streetViewLocation) {
        try {
          streetViewImage = await getStreetViewImage();
        } catch (error) {
          console.error('Error getting street view image:', error);
        }
      }

      if (streetViewLocation) {
        try {
          staticMapImage = await getStaticMapImage(streetViewLocation.lat, streetViewLocation.lng);
        } catch (error) {
          console.error('Error getting static map image:', error);
        }
      }

      const imageUrls = await saveFormImages(formId, streetViewImage, staticMapImage);
      if (!imageUrls.streetView && savedStreetViewUrl) {
        imageUrls.streetView = savedStreetViewUrl;
      }

      const formRef = doc(db, 'forms', formId);
      const existingDoc = await getDoc(formRef);

      let reportId: string;
      let generatedAt: string | undefined;
      let version: number;
      let createdAt: string;
      let wasExistingDoc = false;
      let existingDocData: Record<string, any> | null = null;

      if (existingDoc.exists()) {
        wasExistingDoc = true;
        existingDocData = existingDoc.data() as Record<string, any>;
        reportId = existingDocData.reportId || formId;
        generatedAt = existingDocData.generatedAt;
        version = existingDocData.version || 1;
        createdAt = existingDocData.createdAt || existingDocData.timestamp || new Date().toISOString();

        console.log('[Save] updating existing doc | final propertyImages:', imageUrls.propertyImages.length, imageUrls.propertyImages);
        await setDoc(formRef, {
          formId,
          userId: auth.currentUser?.uid,
          createdAt,
          reportId,
          generatedAt,
          lastModified: new Date().toISOString(),
          version,
          status: 'draft',
          street: selectedStreet?.value,
          streetLabel: selectedStreet?.label,
          address: {
            city: selectedCity?.label,
            neighborhood: selectedNeighborhood?.label,
            street: selectedStreet?.label,
            houseNumber: formData.houseNumber
          },
          property: propertyDataForFirestore,
          imageUrls: {
            propertyImages: imageUrls.propertyImages || [],
            streetView: imageUrls.streetView || null,
            staticMap: imageUrls.staticMap || null
          },
          nearbyPlaces: nearbyPlaces || {}
        });
      } else {
        const now = new Date().toISOString();
        reportId = formId;
        version = 1;
        createdAt = now;

        await setDoc(formRef, {
          formId,
          userId: auth.currentUser?.uid,
          createdAt,
          reportId,
          lastModified: now,
          version,
          status: 'draft',
          street: selectedStreet?.value,
          streetLabel: selectedStreet?.label,
          address: {
            city: selectedCity?.label,
            neighborhood: selectedNeighborhood?.label,
            street: selectedStreet?.label,
            houseNumber: formData.houseNumber
          },
          property: propertyDataForFirestore,
          imageUrls: {
            propertyImages: imageUrls.propertyImages || [],
            streetView: imageUrls.streetView || null,
            staticMap: imageUrls.staticMap || null
          },
          nearbyPlaces: nearbyPlaces || {}
        });
      }

      toast.success('נתוני הנכס נשמרו בהצלחה');
      setHasUnsavedImageChanges(false);

      // Smart Alerts: trigger match detection after property save
      const savedPropertyData = {
        property: propertyDataForFirestore,
        address: {
          city: selectedCity?.label,
          neighborhood: selectedNeighborhood?.label,
        },
      };
      if (wasExistingDoc && existingDocData) {
        if (propertyRelevantFieldsChanged(existingDocData, savedPropertyData)) {
          triggerMatchDetection('property_updated', formId, 'property');
          setTimeout(() => window.dispatchEvent(new Event('smart-alerts:refresh')), 3000);
        }
      } else {
        triggerMatchDetection('property_new', formId, 'property');
        setTimeout(() => window.dispatchEvent(new Event('smart-alerts:refresh')), 3000);
      }
    } catch (error) {
      console.error('Error saving data:', error);
      toast.error('שגיאה בשמירת נתוני הנכס');
    } finally {
      setIsSaving(false);
    }
  };

  const checkForDuplicateReport = (): boolean => {
    const currentReportData = JSON.stringify({
      city: selectedCity?.label,
      neighborhood: selectedNeighborhood?.label,
      street: selectedStreet?.label,
      houseNumber: formData.houseNumber,
      propertyType: formData.propertyType,
      rooms: formData.rooms,
      size: formData.size,
      price: formData.price,
      advisorPrice: formData.advisorPrice,
      floor: formData.floor,
    });

    if (lastReportData === currentReportData) {
      const shouldContinue = window.confirm(
        '⚠️ שים לב!\n\n' +
        'הנתונים זהים לדוח הקודם שהפקת.\n' +
        'האם אתה בטוח שברצונך להפיק דוח נוסף עם אותם נתונים?\n\n' +
        'הפקת דוח תצרוך דוח נוסף מהמכסה שלך.'
      );

      if (!shouldContinue) {
        return false;
      }
    }

    setLastReportData(currentReportData);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!auth.currentUser) {
      toast.error('יש להתחבר מחדש');
      return;
    }

    if (isSubmitting) {
      return;
    }

    try {
      setIsSubmitting(true);

      if (!validateForm()) {
        return;
      }

      if (!checkForDuplicateReport()) {
        return;
      }

      const canSubmit = await updateReportCount();
      if (!canSubmit) {
        return;
      }

      const formId = generateFormId(userProfile?.phone || '');
      const timestamp = new Date().toISOString();
      let streetViewImage: File | undefined;
      let staticMapImage: File | undefined;

      if (streetViewLocation) {
        try {
          streetViewImage = await getStreetViewImage();
        } catch (error) {
          console.error('Error getting street view image:', error);
        }
      }

      if (streetViewLocation) {
        try {
          staticMapImage = await getStaticMapImage(streetViewLocation.lat, streetViewLocation.lng);
        } catch (error) {
          console.error('Error getting static map image:', error);
        }
      }

      const imageUrls = await saveFormImages(formId, streetViewImage, staticMapImage);
      if (!imageUrls.streetView && savedStreetViewUrl) {
        imageUrls.streetView = savedStreetViewUrl;
      }

      const streetViewImageUrl = imageUrls.streetView || (streetViewLocation
        ? `https://maps.googleapis.com/maps/api/streetview?size=800x600&location=${streetViewLocation.lat},${streetViewLocation.lng}&heading=${streetViewLocation.heading}&pitch=${streetViewLocation.pitch}&key=${GOOGLE_MAPS_API_KEY}`
        : null);

      const webhookData = {
        formId,
        userId: auth.currentUser.uid,
        userEmail: formData.email,
        userName,
        logoUrl: userProfile?.logoUrl,
        timestamp,
        createdAt,
        lastModified: new Date().toISOString(),
        street: selectedStreet.value,
        streetLabel: selectedStreet.label,
        neighborhoodMapUrl,
        neighborhoodDealsCount,
        property: {
          city: selectedCity.label,
          neighborhood: selectedNeighborhood?.label || '',
          street: selectedStreet.label,
          houseNumber: formData.houseNumber,
          propertyType: selectedPropertyType?.label || '',
          propertyAge: selectedPropertyAge?.label || '',
          propertyCondition: selectedPropertyCondition?.value || 'unknown',
          propertyConditionLabel: selectedPropertyCondition?.label || '',
          view: selectedView?.label || '',
          airDirection: selectedAirDirection?.label || '',
          rooms: formData.rooms,
          floor: formData.floor,
          bathrooms: formData.bathrooms,
          parkingSpots: formData.parkingSpots,
          size: formData.size,
          price: formData.price,
          advisorPrice: formData.advisorPrice ?? 0,
          description: formData.description || '',
          hasBalcony: formData.hasBalcony,
          balconySize: formData.balconySize ?? null,
          hasElevator: formData.hasElevator,
          hasSaferoom: formData.hasSaferoom,
          daysOnMarket: formData.daysOnMarket,
          images: imageUrls.propertyImages,
          streetView: imageUrls.streetView,
          staticMap: imageUrls.staticMap,
          streetViewImageUrl,
          nearbyPlaces
        }
      };

      const response = await fetch(MAKE_PROPERTY_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookData),
      });

      if (!response.ok) {
        throw new Error('Failed to send data to webhook');
      }

      await updateCreateAdCount();

      setFormData({
        city: '',
        cityLabel: '',
        neighborhood: '',
        neighborhoodLabel: '',
        street: '',
        streetLabel: '',
        houseNumber: '',
        rooms: 1,
        bathrooms: 1,
        parkingSpots: 0,
        size: 0,
        price: 0,
        advisorPrice: undefined,
        floor: undefined,
        propertyType: '',
        propertyTypeLabel: '',
        description: '',
        email: formData.email,
        images: [],
        propertyAge: '',
        propertyAgeLabel: '',
        propertyCondition: '',
        propertyConditionLabel: '',
        view: '',
        viewLabel: '',
        airDirection: '',
        airDirectionLabel: '',
        hasElevator: false,
        hasSaferoom: false,
        hasBalcony: false,
        balconySize: undefined,
        daysOnMarket: ''
      });

      setSelectedCity(null);
      setSelectedNeighborhood(null);
      setSelectedStreet(null);
      setSelectedPropertyType(null);
      setSelectedPropertyAge(null);
      setSelectedPropertyCondition(null);
      setSelectedView(null);
      setSelectedAirDirection(null);
      setNearbyPlaces({});
      setStreetViewAvailable(false);
      setStreetViewLocation(null);
      setSavedStreetViewUrl(null);
      setCurrentAddress('');
      setNeighborhoodMapUrl(null);
      setNeighborhoodDealsCount(null);
      setIsLoadedReport(false);
      setExistingImageUrls([]);
      setLoadedFormId(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      toast.success('הטופס נשלח בהצלחה');
      setHasUnsavedImageChanges(false);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('אירעה שגיאה בשליחת הטופס');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPlanName = (planId: string): string => {
    switch (planId) {
      case 'basic': return 'חבילה בסיסית';
      case 'pro': return 'חבילה מקצועית';
      case 'premium': return 'חבילה פרימיום';
      default: return 'חבילה לא ידועה';
    }
  };

  const RV_LOGO_URL = 'https://firebasestorage.googleapis.com/v0/b/realty-value-42d6d.firebasestorage.app/o/RV%20LOGO%2FRV_LOGO.png?alt=media';

  return (
    <div className="min-h-screen bg-gray-900 text-white" dir="rtl">
      {/* ── Top Navigation Bar ── */}
      <header className="bg-gray-900/95 backdrop-blur-sm border-b border-gray-700/50 sticky top-0 z-20">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-11 gap-2">
            {/* Brand mark — RV logo + product name */}
            <div className="flex items-center gap-2 shrink-0">
              <span className="font-semibold text-sm text-white tracking-tight">Realty Value</span>
              <img src={RV_LOGO_URL} alt="RV" className="h-12 w-auto object-contain" />
            </div>
            {/* Action buttons — all neutral, no active state on homepage */}
            <div className="flex items-center gap-1 flex-wrap justify-end">
              {officeRole !== 'agent' && (
                <button
                  onClick={() => navigate('/office')}
                  className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-transparent hover:bg-gray-800 border border-transparent hover:border-gray-700 text-gray-400 hover:text-gray-200 rounded-md transition-all"
                >
                  <Building2 size={13} />
                  ניהול משרד
                </button>
              )}
              {(officeRole === 'owner' || officeRole === 'manager') && (
                <button
                  onClick={() => navigate('/dashboard')}
                  className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-transparent hover:bg-gray-800 border border-transparent hover:border-gray-700 text-gray-400 hover:text-gray-200 rounded-md transition-all"
                >
                  <LayoutDashboard size={13} />
                  דשבורד משרד
                </button>
              )}
              <button
                onClick={() => navigate('/settings')}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-transparent hover:bg-gray-800 border border-transparent hover:border-gray-700 text-gray-400 hover:text-gray-200 rounded-md transition-all"
              >
                <Share2 size={13} />
                חיבור לפייסבוק
              </button>
              <button
                onClick={() => navigate('/network')}
                className="relative flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-transparent hover:bg-gray-800 border border-transparent hover:border-gray-700 text-gray-400 hover:text-gray-200 rounded-md transition-all"
              >
                <Globe size={13} />
                RV Network
                {networkNotifs.counts.total > 0 && (
                  <span className="absolute -top-1.5 -left-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full ring-2 ring-gray-900">
                    {networkNotifs.counts.total > 99 ? '99+' : networkNotifs.counts.total}
                  </span>
                )}
              </button>
              <NotificationBell
                onOpenMatch={(alert) => {
                  setAutoOpenMatchLeadId(alert.buyer_id);
                  setShowCRM(true);
                }}
              />
              <button
                onClick={() => setShowCRM(true)}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-transparent hover:bg-gray-800 border border-transparent hover:border-gray-700 text-gray-400 hover:text-gray-200 rounded-md transition-all"
              >
                <Users size={13} />
                Leads & Actions
              </button>
              <button
                onClick={() => { if (confirmDiscardImages()) setShowHistory(true); }}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-transparent hover:bg-gray-800 border border-transparent hover:border-gray-700 text-gray-400 hover:text-gray-200 rounded-md transition-all"
              >
                <History size={13} />
                Property Actions
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-transparent hover:bg-red-900/30 border border-transparent hover:border-red-800/50 text-gray-500 hover:text-red-400 rounded-md transition-all"
              >
                <LogOut size={13} />
                התנתקות
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ── Hero Section ── */}
      <div
        className="relative py-10 bg-cover bg-center"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=2000&q=80)' }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-gray-950/85 via-gray-900/80 to-gray-900/95" />
        <div className="relative container mx-auto px-4 text-center">
          <div className="mb-7">
            <img
              src={RV_LOGO_URL}
              alt="Realty Value"
              className="w-48 sm:w-64 h-auto mx-auto object-contain"
            />
          </div>
          <h1 className="text-5xl font-bold tracking-tight text-white mb-2">Realty Value</h1>
          <p className="text-sm text-gray-500">מערכת AI מתקדמת לניתוח ומידע על נכסים</p>
          {officeName && (
            <div className="mt-4">
              <span className="inline-flex items-center px-3 py-1.5 bg-gray-800/80 border border-gray-700/60 rounded-full text-sm text-gray-300 font-medium backdrop-blur-sm">
                {officeName}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ── Owner expired-commercial banner ── */}
      <OwnerExpiredBanner />

      {/* ── Onboarding for users without an office ── */}
      {officeChecked && entResolutionState === 'resolved' && !hasOffice && pendingInvitations.length === 0 && (
        <div className="bg-gray-900 border-b border-gray-800" dir="rtl">
          <div className="container mx-auto px-4 py-10">
            <div className="max-w-2xl mx-auto">
              {onboardingChoice === 'none' && (
                <div className="bg-gray-800/80 border border-gray-700/60 rounded-2xl p-6 sm:p-8">
                  <div className="text-center mb-6">
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-blue-500/10 flex items-center justify-center mb-3">
                      <Sparkles size={26} className="text-blue-400" />
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-2">ברוכים הבאים ל-RV</h2>
                    <p className="text-sm text-gray-400">כדי להתחיל לעבוד במערכת יש לבחור כיצד אתה מצטרף ל-RV.</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {/* Option A: Office owner */}
                    <div className="bg-gray-700/30 border border-gray-700 rounded-xl p-5 flex flex-col">
                      <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center mb-3">
                        <Building2 size={20} className="text-amber-400" />
                      </div>
                      <h3 className="text-base font-bold text-white mb-1.5">אני בעל משרד / מקים משרד חדש</h3>
                      <p className="text-xs text-gray-400 mb-4 flex-1 leading-relaxed">
                        אם אתה בעל משרד או מנהל שמקים סביבת עבודה חדשה, יש להקים את המשרד במערכת.
                      </p>
                      <button
                        onClick={() => setOnboardingChoice('create')}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors text-sm font-semibold"
                      >
                        <Plus size={16} />
                        הקם משרד חדש
                      </button>
                    </div>

                    {/* Option B: Developer / contractor company */}
                    <div className="bg-gray-700/30 border border-gray-700 rounded-xl p-5 flex flex-col">
                      <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center mb-3">
                        <Briefcase size={20} className="text-emerald-400" />
                      </div>
                      <h3 className="text-base font-bold text-white mb-1.5">אני חברת יזמות / קבלן</h3>
                      <p className="text-xs text-gray-400 mb-4 flex-1 leading-relaxed">
                        אם אתה יזם או קבלן המקים חברה לניהול פרויקטים ויחידות מכירה, יש להקים חברה במערכת.
                      </p>
                      <button
                        onClick={() => setOnboardingChoice('create_developer')}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl transition-colors text-sm font-semibold"
                      >
                        <Plus size={16} />
                        הקם חברה חדשה
                      </button>
                    </div>

                    {/* Option C: Agent joining existing office */}
                    <div className="bg-gray-700/30 border border-gray-700 rounded-xl p-5 flex flex-col">
                      <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center mb-3">
                        <Users size={20} className="text-blue-400" />
                      </div>
                      <h3 className="text-base font-bold text-white mb-1.5">אני סוכן במשרד קיים</h3>
                      <p className="text-xs text-gray-400 mb-4 flex-1 leading-relaxed">
                        אם אתה סוכן במשרד שכבר עובד עם RV, אין להקים משרד חדש. מנהל המשרד צריך לצרף אותך למשרד הקיים.
                      </p>
                      <button
                        onClick={() => setOnboardingChoice('wait')}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-600 hover:bg-gray-500 text-white rounded-xl transition-colors text-sm font-medium"
                      >
                        <Clock size={16} />
                        אני ממתין לצירוף למשרד
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {onboardingChoice === 'create' && (
                <div className="bg-gray-800/80 border border-gray-700/60 rounded-2xl p-6 sm:p-8">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                      <Building2 size={20} className="text-amber-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">הקמת משרד חדש</h3>
                      <p className="text-xs text-gray-400">הינך מוגדר כבעלים וכחבר הראשון במשרד. תקופת ניסיון 30 ימים תתחיל מיד.</p>
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-300 mb-2">שם המשרד</label>
                    <input
                      type="text"
                      id="onboarding-office-name"
                      className="w-full px-3 py-2.5 rounded-lg bg-gray-700 border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-white"
                      placeholder="לדוגמה: רימקס תל אביב"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      disabled={creatingOffice}
                      onClick={async () => {
                        const nameInput = document.getElementById('onboarding-office-name') as HTMLInputElement;
                        const officeNameVal = nameInput?.value?.trim();
                        if (!officeNameVal) { toast.error('יש להזין שם משרד'); return; }
                        try {
                          setCreatingOffice(true);
                          const token = await auth.currentUser?.getIdToken();
                          const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-office`, {
                            method: 'POST',
                            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                            body: JSON.stringify({ name: officeNameVal }),
                          });
                          const data = await res.json();
                          if (!res.ok) { toast.error(data.error || 'שגיאה ביצירת משרד'); return; }
                          toast.success('המשרד הוקם בהצלחה! תקופת הניסיון שלך ל-30 יום התחילה.');
                          setOnboardingChoice('none');
                          // Await entitlement refresh so officeRole/officeName and all
                          // Dashboard values update before we stop showing the loading state.
                          await refreshEntitlement();
                        } catch { toast.error('שגיאה ביצירת משרד'); }
                        finally { setCreatingOffice(false); }
                      }}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors text-sm font-semibold disabled:opacity-50"
                    >
                      {creatingOffice ? 'מקים משרד...' : (<><Plus size={16} /> הקם משרד עכשיו</>)}
                    </button>
                    <button
                      onClick={() => setOnboardingChoice('none')}
                      className="px-4 py-2.5 text-sm text-gray-400 hover:text-white border border-gray-700 rounded-xl transition-colors"
                    >
                      חזרה
                    </button>
                  </div>
                </div>
              )}

              {onboardingChoice === 'create_developer' && (
                <div className="bg-gray-800/80 border border-gray-700/60 rounded-2xl p-6 sm:p-8">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
                      <Briefcase size={20} className="text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">הקמת חברת יזמות חדשה</h3>
                      <p className="text-xs text-gray-400">הינך מוגדר כבעלים וכחבר הראשון בחברה. תקופת ניסיון 30 ימים תתחיל מיד.</p>
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-300 mb-2">שם החברה</label>
                    <input
                      type="text"
                      id="onboarding-developer-name"
                      className="w-full px-3 py-2.5 rounded-lg bg-gray-700 border border-gray-600 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 text-white"
                      placeholder="לדוגמה: אזורים נדל״ן בע״מ"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">טלפון (אופציונלי)</label>
                      <input
                        type="tel"
                        id="onboarding-developer-phone"
                        className="w-full px-3 py-2.5 rounded-lg bg-gray-700 border border-gray-600 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 text-white text-right"
                        placeholder="03-123-4567"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">דואר אלקטרוני (אופציונלי)</label>
                      <input
                        type="email"
                        id="onboarding-developer-email"
                        className="w-full px-3 py-2.5 rounded-lg bg-gray-700 border border-gray-600 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 text-white"
                        placeholder="info@example.co.il"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button
                      disabled={creatingOffice}
                      onClick={async () => {
                        const nameInput = document.getElementById('onboarding-developer-name') as HTMLInputElement;
                        const phoneInput = document.getElementById('onboarding-developer-phone') as HTMLInputElement;
                        const emailInput = document.getElementById('onboarding-developer-email') as HTMLInputElement;
                        const officeNameVal = nameInput?.value?.trim();
                        if (!officeNameVal) { toast.error('יש להזין שם חברה'); return; }
                        const payload: Record<string, string> = { name: officeNameVal, accountType: 'developer' };
                        const phoneVal = phoneInput?.value?.trim();
                        const emailVal = emailInput?.value?.trim();
                        if (phoneVal) payload.phone = phoneVal;
                        if (emailVal) payload.email = emailVal;
                        try {
                          setCreatingOffice(true);
                          const token = await auth.currentUser?.getIdToken();
                          const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-office`, {
                            method: 'POST',
                            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                            body: JSON.stringify(payload),
                          });
                          const data = await res.json();
                          if (!res.ok) { toast.error(data.error || 'שגיאה ביצירת חברה'); return; }
                          toast.success('החברה הוקמה בהצלחה! תקופת הניסיון שלך ל-30 יום התחילה.');
                          setOnboardingChoice('none');
                          await refreshEntitlement();
                        } catch { toast.error('שגיאה ביצירת חברה'); }
                        finally { setCreatingOffice(false); }
                      }}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl transition-colors text-sm font-semibold disabled:opacity-50"
                    >
                      {creatingOffice ? 'מקים חברה...' : (<><Plus size={16} /> הקם חברה עכשיו</>)}
                    </button>
                    <button
                      onClick={() => setOnboardingChoice('none')}
                      className="px-4 py-2.5 text-sm text-gray-400 hover:text-white border border-gray-700 rounded-xl transition-colors"
                    >
                      חזרה
                    </button>
                  </div>
                </div>
              )}

              {onboardingChoice === 'wait' && (
                <div className="bg-gray-800/80 border border-gray-700/60 rounded-2xl p-6 sm:p-8">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                      <Clock size={20} className="text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">ממתין לשיוך למשרד</h3>
                      <p className="text-xs text-gray-400">בקש מבעל המשרד או ממנהל המשרד לצרף אותך.</p>
                    </div>
                  </div>
                  <div className="bg-gray-700/40 border border-gray-700 rounded-xl p-4 mb-5">
                    <p className="text-xs text-gray-500 mb-1">האימייל שלך:</p>
                    <p className="text-sm text-white font-medium flex items-center gap-2">
                      <Mail size={14} className="text-gray-400" />
                      {auth.currentUser?.email}
                    </p>
                    <p className="text-xs text-gray-400 mt-2 leading-relaxed">
                      בקש מבעל המשרד או ממנהל המשרד לצרף את כתובת האימייל שלך למשרד הקיים ב-RV.
                    </p>
                  </div>
                  <button
                    onClick={() => setOnboardingChoice('none')}
                    className="inline-flex items-center gap-2 px-4 py-2.5 text-sm text-gray-400 hover:text-white border border-gray-700 rounded-xl transition-colors"
                  >
                    <ArrowRight size={15} />
                    חזרה לבחירה
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Invitation acceptance view for no-office users with pending invitations ── */}
      {officeChecked && entResolutionState === 'resolved' && !hasOffice && pendingInvitations.length > 0 && (
        <div className="min-h-[60vh] flex items-center justify-center px-4 py-12" dir="rtl">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6 sm:p-8 max-w-md w-full">
            <div className="text-center mb-6">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-blue-500/10 flex items-center justify-center mb-3">
                <Mail size={26} className="text-blue-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-1">מצאנו הזמנה עבורך</h2>
              <p className="text-sm text-gray-400">קיבלת הזמנה להצטרף למשרד</p>
            </div>
            {pendingInvitations.map(inv => (
              <div key={inv.id} className="bg-gray-700/60 border border-gray-600 rounded-lg p-4 mb-3">
                <p className="text-sm text-gray-200 mb-1">
                  <span className="font-semibold text-white">{inv.office_name}</span> מזמינים אותך להצטרף כ
                  <span className="font-semibold text-blue-300 mr-1">
                    {inv.role === 'owner' ? 'בעלים' : inv.role === 'manager' ? 'מנהל' : 'סוכן'}
                  </span>
                </p>
                <button
                  onClick={() => handleAcceptInvitation(inv.invite_token)}
                  disabled={respondingInvite === inv.invite_token}
                  className="w-full mt-3 flex items-center justify-center gap-2 px-3 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors"
                >
                  {respondingInvite === inv.invite_token ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <CheckCircle size={16} />
                  )}
                  הצטרף למשרד
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Account Summary (Office-oriented) — only for users with an office ── */}
      {userProfile && hasOffice && (
        <div className="bg-gray-900 py-5 border-b border-gray-800">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* User greeting + role */}
              <div className="bg-gray-800/70 border border-gray-700/50 rounded-xl p-4 flex flex-col hover:border-gray-600/70 transition-colors min-h-[100px]">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-md bg-blue-500/10 flex items-center justify-center shrink-0">
                    <User size={13} className="text-blue-400" />
                  </div>
                  <span className="text-xs text-gray-500">משתמש</span>
                </div>
                <p className="text-xl font-bold text-white leading-none mb-1">שלום, {userName}</p>
                <p className="text-xs text-gray-400 mt-auto">
                  {entitlement.office_name ? `${entitlement.office_name}` : 'אין משרד'}
                  {entitlement.my_role && (
                    <span className="text-gray-500"> · {entitlement.my_role === 'owner' ? 'בעלים' : entitlement.my_role === 'manager' ? 'מנהל' : entitlement.my_role === 'agent' ? 'סוכן' : entitlement.my_role}</span>
                  )}
                </p>
              </div>

              {/* Trial / Subscription status */}
              <div className="bg-gray-800/70 border border-gray-700/50 rounded-xl p-4 flex flex-col hover:border-gray-600/70 transition-colors min-h-[100px]">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-md bg-amber-500/10 flex items-center justify-center shrink-0">
                    <Calendar size={13} className="text-amber-400" />
                  </div>
                  <span className="text-xs text-gray-500">סטטוס מנוי</span>
                </div>
                {entitlement.is_internal ? (
                  <p className="text-lg font-bold text-white leading-none mb-1">חשבון פנימי</p>
                ) : entitlement.is_trial_active ? (
                  <>
                    <p className="text-lg font-bold text-white leading-none mb-1">תקופת ניסיון</p>
                    <p className="text-xs text-gray-400 mt-auto">
                      {entitlement.trial_days_remaining != null ? `נותרו ${entitlement.trial_days_remaining} ימים` : ''}
                    </p>
                  </>
                ) : entitlement.subscription_status === 'active' ? (
                  <p className="text-lg font-bold text-white leading-none mb-1">מנוי פעיל</p>
                ) : entitlement.subscription_status ? (
                  <p className="text-lg font-bold text-white leading-none mb-1">{entitlement.subscription_status}</p>
                ) : (
                  <p className="text-lg font-bold text-white leading-none mb-1">ללא משרד</p>
                )}
              </div>

              {/* Seats */}
              <div className="bg-gray-800/70 border border-gray-700/50 rounded-xl p-4 flex flex-col hover:border-gray-600/70 transition-colors min-h-[100px]">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-md bg-blue-500/10 flex items-center justify-center shrink-0">
                    <Users size={13} className="text-blue-400" />
                  </div>
                  <span className="text-xs text-gray-500">משתמשים במשרד</span>
                </div>
                <p className="text-3xl font-bold text-white leading-none tabular-nums mb-1">
                  {entitlement.active_seat_count != null ? entitlement.active_seat_count : '-'}
                </p>
                <p className="text-xs text-gray-400 mt-auto">מתוך {entitlement.seat_limit ?? '-'}</p>
              </div>

              {/* Access level */}
              <div className="bg-gray-800/70 border border-gray-700/50 rounded-xl p-4 flex flex-col hover:border-gray-600/70 transition-colors min-h-[100px]">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-md bg-green-500/10 flex items-center justify-center shrink-0">
                    <Package size={13} className="text-green-400" />
                  </div>
                  <span className="text-xs text-gray-500">רמת גישה</span>
                </div>
                <p className="text-lg font-bold text-white leading-none mb-1">
                  {entitlement.is_read_only ? 'קריאה בלבד' : 'גישה מלאה'}
                </p>
                <p className="text-xs text-gray-400 mt-auto">
                  {entitlement.can_use_ai ? 'AI מאופשר' : 'AI לא זמין'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {hasOffice && (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto bg-gray-800 rounded-lg shadow-xl p-8">
          {/* Debug Info */}
          <div className="mb-6 p-4 bg-gray-700 rounded-lg">
            <h3 className="text-lg font-bold mb-2 text-center">מידע מערכת</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center justify-between">
                <span>Google Maps Loaded:</span>
                <span className={`w-3 h-3 rounded-full ${isGoogleMapsLoaded ? 'bg-green-500' : 'bg-red-500'}`}></span>
              </div>
              <div className="flex items-center justify-between">
                <span>Current Address:</span>
                <span className="text-xs">{currentAddress || 'לא נבחר'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Map Loading:</span>
                <span className={`w-3 h-3 rounded-full ${mapLoading ? 'bg-yellow-500' : 'bg-gray-500'}`}></span>
              </div>
              <div className="flex items-center justify-between">
                <span>Street View Location:</span>
                <span className="text-xs">{streetViewLocation ? `${streetViewLocation.lat.toFixed(6)}, ${streetViewLocation.lng.toFixed(6)}` : 'לא זמין'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Street View Available:</span>
                <span className={`w-3 h-3 rounded-full ${streetViewAvailable ? 'bg-green-500' : 'bg-red-500'}`}></span>
              </div>
              <div className="flex items-center justify-between">
                <span>Neighborhood Map:</span>
                <span className={`w-3 h-3 rounded-full ${neighborhoodMapUrl ? 'bg-green-500' : 'bg-red-500'}`}></span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Logo and Neighborhood Map Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium mb-2">לוגו שיופיע על הדו"ח</label>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      className="flex items-center gap-2 px-3.5 h-10 bg-transparent border border-white/[0.12] rounded-lg text-sm font-medium text-gray-200 hover:bg-white/[0.06] hover:border-white/[0.22] transition-all"
                    >
                      <Upload size={15} />
                      העלאת לוגו
                    </button>
                  </div>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleLogoUpload}
                  />
                  {(logoFile || userProfile?.logoUrl) && (
                    <div className="relative inline-block">
                      <img
                        src={logoFile ? URL.createObjectURL(logoFile) : userProfile?.logoUrl}
                        alt="לוגו"
                        className="w-24 h-24 object-contain rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={removeLogo}
                        className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1 hover:bg-red-600 transition-colors"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">מפת גבולות השכונה</label>
                <NeighborhoodMap
                  neighborhood={selectedNeighborhood?.label || ''}
                  city={selectedCity?.label || ''}
                  onMapUrlChange={setNeighborhoodMapUrl}
                  onDealsCountChange={setNeighborhoodDealsCount}
                />
              </div>
            </div>

            <div className="flex justify-center mb-6">
              <DateDisplay />
            </div>

            {!isLoadedReport && (
              <div className="mb-6 p-4 bg-yellow-900/30 border-2 border-yellow-600 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="text-yellow-400 text-2xl">⚠️</div>
                  <div className="flex-1">
                    <h3 className="text-yellow-400 font-bold text-lg mb-1">שים לב!</h3>
                    <p className="text-yellow-100">
                      לאחר שמירת הנכס, <strong>הכתובת תינעל</strong> ולא תוכל לשנותה (עיר, שכונה, רחוב ומספר בית).
                      <br />
                      ודא שהכתובת נכונה לפני השמירה!
                    </p>
                  </div>
                </div>
              </div>
            )}

            {isLoadedReport && (
              <div className="mb-6 p-4 bg-blue-900/30 border-2 border-blue-600 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="text-blue-400 text-2xl">🔒</div>
                  <div className="flex-1">
                    <h3 className="text-blue-400 font-bold text-lg mb-1">הכתובת נעולה</h3>
                    <p className="text-blue-100">
                      כתובת הנכס נעולה ולא ניתן לשינוי. כתובת הנכס מזהה אותו באופן ייחודי.
                      <br />
                      <strong>טעית בכתובת?</strong> לחץ על כפתור <strong>"נקה טופס"</strong> למטה והתחל מחדש.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">
                  עיר
                  {isLoadedReport && <span className="text-yellow-400 mr-2">🔒</span>}
                </label>
                <Select
                  options={cities}
                  value={selectedCity}
                  onChange={(option) => {
                    setSelectedCity(option);
                    setSelectedNeighborhood(null);
                    setSelectedStreet(null);
                    setFormData(prev => ({
                      ...prev,
                      city: option?.value || '',
                      cityLabel: option?.label || '',
                      neighborhood: '',
                      neighborhoodLabel: '',
                      street: '',
                      streetLabel: ''
                    }));
                  }}
                  placeholder="בחר עיר"
                  className="select-container"
                  classNamePrefix="select"
                  isDisabled={isLoadedReport}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  שכונה
                  {isLoadedReport && <span className="text-yellow-400 mr-2">🔒</span>}
                </label>
                <Select
                  options={selectedCity ? neighborhoods[selectedCity.value] : []}
                  value={selectedNeighborhood}
                  onChange={(option) => {
                    setSelectedNeighborhood(option);
                    setSelectedStreet(null);
                    setFormData(prev => ({
                      ...prev,
                      neighborhood: option?.value || '',
                      neighborhoodLabel: option?.label || '',
                      street: '',
                      streetLabel: ''
                    }));
                  }}
                  placeholder="בחר שכונה"
                  className="select-container"
                  classNamePrefix="select"
                  isDisabled={!selectedCity || isLoadedReport}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  רחוב
                  {isLoadedReport && <span className="text-yellow-400 mr-2">🔒</span>}
                </label>
                {(() => {
                  const streetOptions = selectedCity && selectedNeighborhood
                    ? (streets[selectedCity.value]?.[selectedNeighborhood.value] ?? [])
                    : [];
                  if (streetOptions.length > 0) {
                    return (
                      <Select
                        options={streetOptions}
                        value={selectedStreet}
                        onChange={(option) => {
                          setSelectedStreet(option);
                          setFormData(prev => ({
                            ...prev,
                            street: option?.value || '',
                            streetLabel: option?.label || ''
                          }));
                        }}
                        placeholder="בחר רחוב"
                        className="select-container"
                        classNamePrefix="select"
                        isDisabled={!selectedNeighborhood || isLoadedReport}
                      />
                    );
                  }
                  return (
                    <input
                      type="text"
                      className="w-full px-3 py-2 rounded-md bg-gray-700 border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      value={selectedStreet?.label ?? ''}
                      onChange={(e) => {
                        const typed = e.target.value;
                        const opt = typed ? { value: typed, label: typed } : null;
                        setSelectedStreet(opt);
                        setFormData(prev => ({
                          ...prev,
                          street: typed,
                          streetLabel: typed
                        }));
                      }}
                      placeholder="הזן שם רחוב"
                      disabled={!selectedNeighborhood || isLoadedReport}
                    />
                  );
                })()}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  מספר בית
                  {isLoadedReport && <span className="text-yellow-400 mr-2">🔒</span>}
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 rounded-md bg-gray-700 border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  value={formData.houseNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, houseNumber: e.target.value }))}
                  placeholder="הזן מספר בית"
                  required
                  disabled={isLoadedReport}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">מספר הימים שהנכס נמצא למכירה בשוק</label>
                <input
                  type="number"
                  className="w-full px-3 py-2 rounded-md bg-gray-700 border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  value={formData.daysOnMarket}
                  onChange={(e) => setFormData(prev => ({ ...prev, daysOnMarket: e.target.value }))}
                  placeholder="הזן מספר ימים"
                  min="0"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">סוג נכס</label>
                <Select
                  options={propertyTypes}
                  value={selectedPropertyType}
                  onChange={(option) => {
                    setSelectedPropertyType(option);
                    setFormData(prev => ({
                      ...prev,
                      propertyType: option?.value || '',
                      propertyTypeLabel: option?.label || ''
                    }));
                  }}
                  placeholder="בחר סוג נכס"
                  className="select-container"
                  classNamePrefix="select"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">גיל הנכס</label>
                <Select
                  options={propertyAgeOptions}
                  value={selectedPropertyAge}
                  onChange={(option) => {
                    setSelectedPropertyAge(option);
                    setFormData(prev => ({
                      ...prev,
                      propertyAge: option?.value || '',
                      propertyAgeLabel: option?.label || ''
                    }));
                  }}
                  placeholder="בחר גיל נכס"
                  className="select-container"
                  classNamePrefix="select"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">מצב הנכס</label>
                <Select
                  options={propertyConditionOptions}
                  value={selectedPropertyCondition}
                  onChange={(option) => {
                    setSelectedPropertyCondition(option);
                    setFormData(prev => ({
                      ...prev,
                      propertyCondition: option?.value || '',
                      propertyConditionLabel: option?.label || ''
                    }));
                  }}
                  placeholder="בחר מצב נכס"
                  className="select-container"
                  classNamePrefix="select"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">נוף</label>
                <Select
                  options={viewOptions}
                  value={selectedView}
                  onChange={(option) => {
                    setSelectedView(option);
                    setFormData(prev => ({
                      ...prev,
                      view: option?.value || '',
                      viewLabel: option?.label || ''
                    }));
                  }}
                  placeholder="בחר נוף"
                  className="select-container"
                  classNamePrefix="select"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">כיווני אוויר</label>
                <Select
                  options={airDirectionOptions}
                  value={selectedAirDirection}
                  onChange={(option) => {
                    setSelectedAirDirection(option);
                    setFormData(prev => ({
                      ...prev,
                      airDirection: option?.value || '',
                      airDirectionLabel: option?.label || ''
                    }));
                  }}
                  placeholder="בחר כיווני אוויר"
                  className="select-container"
                  classNamePrefix="select"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">מספר חדרים</label>
                <select
                  className="w-full px-3 py-2 rounded-md bg-gray-700 border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  value={formData.rooms}
                  onChange={(e) => setFormData(prev => ({ ...prev, rooms: Number(e.target.value) }))}
                  required
                >
                  {[...Array(6)].map((_, i) => (
                    <option key={i} value={i + 1}>{i + 1} חדרים</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">חדרי אמבטיה</label>
                <select
                  className="w-full px-3 py-2 rounded-md bg-gray-700 border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  value={formData.bathrooms}
                  onChange={(e) => setFormData(prev => ({ ...prev, bathrooms: Number(e.target.value) }))}
                  required
                >
                  {[...Array(3)].map((_, i) => (
                    <option key={i} value={i + 1}>{i + 1} חדרי אמבטיה</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">חניות</label>
                <select
                  className="w-full px-3 py-2 rounded-md bg-gray-700 border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  value={formData.parkingSpots}
                  onChange={(e) => setFormData(prev => ({ ...prev, parkingSpots: Number(e.target.value) }))}
                  required
                >
                  {[...Array(3)].map((_, i) => (
                    <option key={i} value={i}>{i} חניות</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">הזן גודל במ"ר כפי שמופיע ברישום הנכס בטאבו</label>
                <input
                  type="number"
                  className="w-full px-3 py-2 rounded-md bg-gray-700 border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  value={formData.size || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, size: Number(e.target.value) }))}
                  placeholder="הזן גודל במ״ר"
                  required
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">מחיר מבוקש ע"י בעל הנכס</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 rounded-md bg-gray-700 border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  value={formData.price ? formatPrice(formData.price.toString()) : ''}
                  onChange={handlePriceChange}
                  placeholder="הזן מחיר"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">מחיר על פי הערכת יועץ נדלן</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 rounded-md bg-gray-700 border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  value={formData.advisorPrice ? formatPrice(formData.advisorPrice.toString()) : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    const numericValue = parseInt(value.replace(/[^\d]/g, ''), 10);
                    setFormData(prev => ({
                      ...prev,
                      advisorPrice: isNaN(numericValue) ? undefined : numericValue
                    }));
                  }}
                  placeholder="הזן מחיר"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">דוא״ל אליו ישלח הדו"ח</label>
                <input
                  type="email"
                  className="w-full px-3 py-2 rounded-md bg-gray-700 border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 cursor-not-allowed opacity-75"
                  value={formData.email}
                  readOnly
                  disabled
                />
              </div>
              <div>
  <label className="block text-sm font-medium mb-2">באיזה קומה הנכס?</label>
  <input
    type="number"
    className="w-full px-3 py-2 rounded-md bg-gray-700 border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
    value={formData.floor ?? ''}
    onChange={(e) => setFormData(prev => ({ ...prev, floor: Number(e.target.value) }))}
    placeholder="הזן מספר קומה (0 = קרקע)"
    required
    min="0"
    max="70"
  />
</div>


              <div>
                <label className="block text-sm font-medium mb-2">האם קיימת מרפסת שמש?</label>
                <div className="flex items-center space-x-4 space-x-reverse">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      className="form-radio text-blue-600"
                      name="hasBalcony"
                      checked={formData.hasBalcony}
                      onChange={() => setFormData(prev => ({ ...prev, hasBalcony: true }))}
                    />
                    <span className="mr-2">כן</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      className="form-radio text-blue-600"
                      name="hasBalcony"
                      checked={!formData.hasBalcony}
                      onChange={() => setFormData(prev => ({ ...prev, hasBalcony: false, balconySize: undefined }))}
                    />
                    <span className="mr-2">לא</span>
                  </label>
                </div>
              </div>

              {formData.hasBalcony && (
                <div>
                  <label className="block text-sm font-medium mb-2">גודל מרפסת במ״ר</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 rounded-md bg-gray-700 border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    value={formData.balconySize || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, balconySize: Number(e.target.value) }))}
                    placeholder="הזן גודל במ״ר"
                    required
                    min="0"
                  />
                </div>
              )}
            </div>
<div>
  <label className="block text-sm font-medium mb-2">האם קיים חדר מוגן ?</label>
  <div className="flex items-center space-x-4 space-x-reverse">
    <label className="inline-flex items-center">
      <input
        type="radio"
        className="form-radio text-blue-600"
        name="hasSaferoom"
        checked={formData.hasSaferoom}
        onChange={() => setFormData(prev => ({ ...prev, hasSaferoom: true }))}
      />
      <span className="mr-2">כן</span>
    </label>
    <label className="inline-flex items-center">
      <input
        type="radio"
        className="form-radio text-blue-600"
        name="hasSaferoom"
        checked={!formData.hasSaferoom}
        onChange={() => setFormData(prev => ({ ...prev, hasSaferoom: false }))}
      />
      <span className="mr-2">לא</span>
    </label>
  </div>
</div>
            
  <div>
  <label className="block text-sm font-medium mb-2">האם קיימת מעלית?</label>
  <div className="flex items-center space-x-4 space-x-reverse">
    <label className="inline-flex items-center">
      <input
        type="radio"
        className="form-radio text-blue-600"
        name="hasElevator"
        checked={formData.hasElevator}
        onChange={() => setFormData(prev => ({ ...prev, hasElevator: true }))}
      />
      <span className="mr-2">כן</span>
    </label>
    <label className="inline-flex items-center">
      <input
        type="radio"
        className="form-radio text-blue-600"
        name="hasElevator"
        checked={!formData.hasElevator}
        onChange={() => setFormData(prev => ({ ...prev, hasElevator: false }))}
      />
      <span className="mr-2">לא</span>
    </label>
  </div>
</div>


            <div>
              <label className="block text-sm font-medium mb-2">
                תיאור - פרט האם הנכס משופץ ? האם קיים מחסן או חדר כושר? או כול מידע חשוב נוסף על הנכס
              </label>
              <div className="relative">
                <textarea
                  className="w-full px-3 py-2 rounded-md bg-gray-700 border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 h-32"
                  value={formData.description}
                  onChange={handleDescriptionChange}
                  placeholder="הוסף תיאור לנכס"
                  maxLength={MAX_DESCRIPTION_LENGTH}
                />
                <div className="absolute bottom-2 left-2 text-sm text-gray-400">
                  {remainingChars} תווים נותרו
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">תמונות של הנכס מבפנים</label>
              <div className="space-y-4">
                {/* Count + upload button row */}
                <div className="flex items-center gap-4">
                  {(existingImageUrls.length + formData.images.length) < 5 && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-2 px-3.5 h-10 bg-transparent border border-white/[0.12] rounded-lg text-sm font-medium text-gray-200 hover:bg-white/[0.06] hover:border-white/[0.22] transition-all"
                    >
                      <ImageIcon size={15} />
                      העלאת תמונות
                    </button>
                  )}
                  <span className="text-sm text-gray-400">
                    {existingImageUrls.length + formData.images.length > 0
                      ? `${existingImageUrls.length + formData.images.length} מתוך 5 תמונות`
                      : 'עד 5 תמונות'}
                  </span>
                  {hasUnsavedImageChanges && (
                    <span className="flex items-center gap-1 text-xs font-medium text-amber-400 bg-amber-400/10 border border-amber-400/30 px-2 py-0.5 rounded-full">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                      שינויים לא נשמרו
                    </span>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleImageUpload}
                />
                {/* Existing saved images */}
                {(existingImageUrls.length > 0 || formData.images.length > 0) && (
                  <div className="flex flex-wrap gap-4">
                    {existingImageUrls.map((url, index) => (
                      <div key={`existing-${index}`} className="relative">
                        <img
                          src={url}
                          alt={`תמונה ${index + 1}`}
                          className="w-24 h-24 object-cover rounded-lg"
                        />
                        {index === 0 && (
                          <span className="absolute bottom-1 right-1 bg-blue-600/90 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">
                            ראשית
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => removeExistingImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1 hover:bg-red-600 transition-colors"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                    {/* Newly selected (not yet uploaded) images */}
                    {formData.images.map((image, index) => (
                      <div key={`new-${index}`} className="relative">
                        <img
                          src={URL.createObjectURL(image)}
                          alt={`תמונה ${existingImageUrls.length + index + 1}`}
                          className="w-24 h-24 object-cover rounded-lg ring-2 ring-blue-500/50"
                        />
                        {existingImageUrls.length === 0 && index === 0 && (
                          <span className="absolute bottom-1 right-1 bg-blue-600/90 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">
                            ראשית
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1 hover:bg-red-600 transition-colors"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {hasUnsavedImageChanges && (
                  <p className="text-xs text-amber-400/80 flex items-center gap-1.5">
                    <span>⚠</span>
                    יש לשמור את נתוני הנכס כדי לשמור את שינויי התמונות
                  </p>
                )}
              </div>
            </div>

            {/* Map and Street View Section */}
            {currentAddress && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                      <Map size={20} className="text-blue-400" />
                      מפה
                    </h3>
                    <div className="relative w-full h-80 bg-gray-700 rounded-lg border border-gray-600">
                      <div
                        ref={mapRef}
                        className="absolute inset-0 w-full h-full rounded-lg"
                        style={{ minHeight: '320px' }}
                      />
                      {mapLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-700 rounded-lg">
                          <div className="text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400 mx-auto mb-2"></div>
                            <p className="text-gray-400">טוען מפה...</p>
                          </div>
                        </div>
                      )}
                      {!isGoogleMapsLoaded && !mapLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-700 rounded-lg">
                          <p className="text-gray-400">טוען Google Maps...</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                      <Map size={20} className="text-blue-400" />
                      תמונת רחוב
                    </h3>
                    <div className="relative w-full h-80 bg-gray-700 rounded-lg border border-gray-600">
                      {savedStreetViewUrl ? (
                        <>
                          <img
                            src={savedStreetViewUrl}
                            alt="Street View"
                            className="absolute inset-0 w-full h-full object-cover rounded-lg"
                          />
                          {currentAddress && (
                            <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                              {currentAddress}
                            </div>
                          )}
                          <div className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">
                            &copy; Google Street View
                          </div>
                        </>
                      ) : (
                        <>
                          <div
                            ref={streetViewRef}
                            className="absolute inset-0 w-full h-full rounded-lg"
                            style={{ minHeight: '320px' }}
                          />
                          {mapLoading && (
                            <div className="absolute inset-0 flex items-center justify-center bg-gray-700 rounded-lg">
                              <div className="text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400 mx-auto mb-2"></div>
                                <p className="text-gray-400">טוען Street View...</p>
                              </div>
                            </div>
                          )}
                          {!streetViewAvailable && !mapLoading && isGoogleMapsLoaded && (
                            <div className="absolute inset-0 flex items-center justify-center bg-gray-700 rounded-lg">
                              <p className="text-gray-400">Street View לא זמין למיקום זה</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <button
                type="button"
                onClick={() => { if (confirmDiscardImages()) resetForm(); }}
                className="flex items-center gap-2 px-3.5 h-10 bg-transparent border border-white/[0.12] rounded-lg text-sm font-medium text-gray-200 hover:bg-white/[0.06] hover:border-white/[0.22] transition-all"
              >
                <RotateCcw size={15} />
                נקה טופס
              </button>
              <button
                type="button"
                onClick={handleSaveData}
                disabled={isSaving}
                className="flex items-center gap-2 px-3.5 h-10 bg-transparent border border-green-500/40 rounded-lg text-sm font-medium text-green-300 hover:bg-green-500/10 hover:border-green-500/60 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save size={15} />
                {isSaving ? 'שומר...' : 'שמור נתוני נכס'}
              </button>
              {/* Report button hidden from UI – logic preserved */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="hidden"
              >
                <Send size={15} />
                {isSubmitting ? 'שולח...' : 'הפק דוח נכס'}
                {!isSubmitting && subscription && (
                  <span className="bg-white text-blue-600 px-2 py-1 rounded-md text-sm font-bold">
                    {(subscription.reportsTotal || 0) - (subscription.reportsUsed || 0)}/{subscription.reportsTotal || 0}
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      )}

      {hasOffice && showHistory && auth.currentUser && (
        <ReportHistoryModal
          userId={auth.currentUser.uid}
          onClose={() => setShowHistory(false)}
          onAdCreated={updateCreateAdCount}
          onUpdateAdsCount={updateAdsCount}
          onLoadData={handleLoadData}
          onLeadClick={(leadId) => {
            setShowHistory(false);
            setSelectedLeadId(leadId);
            setShowCRM(true);
          }}
        />
      )}

      {hasOffice && showCRM && auth.currentUser && (
        <ClientCRM
          onClose={() => {
            setShowCRM(false);
            setSelectedLeadId(undefined);
            setAutoOpenMatchLeadId(undefined);
          }}
          selectedLeadId={selectedLeadId}
          autoOpenMatchLeadId={autoOpenMatchLeadId}
        />
      )}

      {/* ── Pending office invitation dialog ── */}
      {showInvitationDialog && pendingInvitations.length > 0 && (
        <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4" dir="rtl">
          <div className="bg-gray-800 border border-gray-700 rounded-xl max-w-sm w-full p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-blue-600/20 flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-bold text-white">מצאנו הזמנה עבורך</h3>
                <p className="text-xs text-gray-400 mt-0.5">קיבלת הזמנה להצטרף למשרד</p>
              </div>
            </div>

            <div className="space-y-3">
              {pendingInvitations.map(inv => (
                <div key={inv.id} className="bg-gray-700/60 border border-gray-600 rounded-lg p-4">
                  <p className="text-sm text-gray-200 mb-1">
                    <span className="font-semibold text-white">{inv.office_name}</span> מזמינים אותך להצטרף כ
                    <span className="font-semibold text-blue-300 mr-1">
                      {inv.role === 'owner' ? 'בעלים' : inv.role === 'manager' ? 'מנהל' : 'סוכן'}
                    </span>
                  </p>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => handleAcceptInvitation(inv.invite_token)}
                      disabled={respondingInvite === inv.invite_token}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      {respondingInvite === inv.invite_token ? (
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      הצטרף למשרד
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => setShowInvitationDialog(false)}
              className="mt-4 w-full text-xs text-gray-500 hover:text-gray-400 transition-colors"
            >
              טפל בהזמנה מאוחר יותר
            </button>
          </div>
        </div>
      )}

      <Toaster position="top-center" />
    </div>
  );
}

export default App;